import styles from "./Tab.module.scss";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { getPickUpOrders, PickUpOrderForm } from '@api/apiClient/pickUp';
import { AddSellerPanel } from "@components/AddSeller";
import { Row, Column } from "@components/Grid";
import Box from "@components/Box";
import { Filters, FilterTypes } from "@containers/Filters";
import { FilterModel } from "@hooks/useFiltersSearchParams";
import DataGrid, { DataViewHandle } from "@containers/DataGrid";
import { Modal } from "@components/Modal";
import PickUpCancelModalWindow from '../modals/PickUpCancelModalWindow';
import PickUpOrderModalWindow from '../modals/PickUpOrderModalWindow/PickUpOrderModalWindow';
import PickUpOrderView from '@components/PickUpOrderView';
import { IOnClickPickUpOrderViewProps } from '@components/PickUpOrderView/types';

const OrdersTab = ({
  isFilterOpen,
  setIsFilterOpen,
  isNew,
  onUpdate,
  sellersNotFound,
}: {
  isFilterOpen: boolean,
  setIsFilterOpen: (v: boolean) => void,
  isNew: boolean,
  sellersNotFound: boolean,
  onUpdate: () => void,
}) => {
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isOpenPickUpCancelWindow, setIsOpenPickUpCancelWindow] = useState<boolean>(false);
  const [isOpenPickUpOrderWindow, setIsOpenPickUpOrderWindow] = useState<boolean>(false);
  const [pickUpOrder, setPickUpOrder] = useState<PickUpOrderForm | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const dataRef = useRef<DataViewHandle>(null);

  const { t } = useTranslation();

  const handlerSetIsOpenPickUpOrderWindow = (data: PickUpOrderForm): void => {
    setPickUpOrder(data);
    setIsOpenPickUpOrderWindow(true);
  };

  const refreshPickUpOrders = (): void => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    onUpdate();
  };

  const onCloseModal = () => {
    refreshPickUpOrders();
    setIsOpenPickUpOrderWindow(false);
  }

  return (
    <div>
      <Box pb={5} className={cn(!isFilterOpen && styles.hidden)}>
        <Column phone={12}>
          <Row justify="right" gap={5}>
            <Filters
              id="pickUp-orders"
              openFilters={() => setIsFilterOpen(true)}
              items={sellersNotFound ? [] : [
                {
                  name: "trackOrExternalNumbers",
                  type: FilterTypes.MULTI_SEARCH,
                  placeholder: t("pickUp.tabOrders.searchByNumber"),
                },
                {
                  name: "sellerIds",
                  type: FilterTypes.SELLER,
                },
                {
                  name: "dateRange",
                  type: FilterTypes.DATE_RANGE,
                  placeholder: t("pickUp.tabOrders.pickUpDate"),
                  fieldNames: ["startDate", "endDate"],
                },
              ]}
              onChange={(v, isReady) => {
                if (isReady) {
                  setIsInitialized(true);
                }
                setInnerFilters(v)
              }}
              values={innerFilters}
              asPanel
            />
          </Row>
        </Column>
      </Box>
      {sellersNotFound ? (
        <AddSellerPanel isFirstSeller />
      ) : (
        <DataGrid<PickUpOrderForm, IOnClickPickUpOrderViewProps<PickUpOrderForm>>
          queryKey="pickUpOrders"
          pending={!isInitialized}
          getData={getPickUpOrders}
          ItemComponent={PickUpOrderView}
          notFound={t("pickUp.tabOrders.noOrders")}
          filters={innerFilters}
          dataRef={dataRef}
          viewProps={{
            onClickOrderView: handlerSetIsOpenPickUpOrderWindow,
          }}
        />
      )}

      <Modal
        bodyWithoutIndentations
        isOpen={isOpenPickUpOrderWindow}
        onClickOverlay={onCloseModal}
        onClose={onCloseModal}
        width={900}
      >
        <PickUpOrderModalWindow
            handlerRefreshPickUpOrders={refreshPickUpOrders}
            handlerSetIsOpenPickUpCancelWindow={setIsOpenPickUpCancelWindow}
            handlerSetIsOpenPickUpOrderWindow={setIsOpenPickUpOrderWindow}
            handlerSetPickUpOrder={setPickUpOrder}
            isNew={isNew}
            pickUpOrder={pickUpOrder!}
            onClose={onCloseModal}
        />
      </Modal>

      <Modal
          isOpen={isOpenPickUpCancelWindow}
          onClickOverlay={() => setIsOpenPickUpCancelWindow(false)}
          onClose={() => setIsOpenPickUpCancelWindow(false)}
      >
        <PickUpCancelModalWindow
            handlerRefreshPickUpOrders={refreshPickUpOrders}
            handlerSetIsOpenPickUpCancelWindow={setIsOpenPickUpCancelWindow}
            pickUpOrder={pickUpOrder!}
        />
      </Modal>
    </div>
  );
};

export default OrdersTab;
